import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { QueryParamsState } from '.'
import { AutocompleteInput } from '../../../inputs/autocomplete-input'
import { WarehouseOption } from './use-warehouse-options'
import { useMemo } from 'react'

type StoreWarehouseFilterProps = {
  name: keyof QueryParamsState
  formContext: UseFormReturn<QueryParamsState, unknown, QueryParamsState>
  warehouseOptions: WarehouseOption[]
}

export const StoreWarehouseFilter = ({
  formContext,
  name,
  warehouseOptions,
}: StoreWarehouseFilterProps) => {
  const { t } = useTranslation(['stock'])

  const selectedValue = formContext.watch(name)
  const selectedOption = useMemo(() => {
    if (!selectedValue) return null
    return (
      warehouseOptions.find((option) => option.id === Number(selectedValue)) ||
      null
    )
  }, [selectedValue, warehouseOptions])

  const inputWidth = useMemo(() => {
    if (!selectedOption) return 'auto'
    const displayText = `${selectedOption.storeName} - ${selectedOption.name}`
    return `${Math.max(displayText.length + 2, 20)}ch`
  }, [selectedOption])

  return (
    <>
      {warehouseOptions.length > 0 && (
        <AutocompleteInput
          control={formContext.control}
          name={name}
          options={warehouseOptions}
          autocompleteProps={{
            sx: {
              width: inputWidth,
              minWidth: '20ch',
              maxWidth: '50ch',
            },
            groupBy: (option) =>
              option.storeName || t('stock:stock_levels.other_warehouses'),
            getOptionLabel: (option) =>
              option?.storeName
                ? `${option.storeName} - ${option.name}`
                : option?.name || '',
            isOptionEqualToValue: (option, value) => option.id === value.id,
            renderOption: ({ key, ...props }, option) => (
              <li {...props} key={key}>
                {option.name}
              </li>
            ),
          }}
          textFieldProps={{
            slotProps: {
              htmlInput: {
                placeholder: t('stock:stock_levels.select_warehouse'),
                ['data-testid']: 'stock-levels-store-select',
              },
            },
          }}
          transform={{
            output: (value) => (value ? String(value.id) : ''),
            input: (value) => {
              if (!value) return null
              return (
                warehouseOptions.find(
                  (option) => option.id === Number(value),
                ) || null
              )
            },
          }}
        />
      )}
    </>
  )
}
